import React from "react";
import { CascadeButton } from "../../../../../components/ui";
import { LINE_ITEM_TYPES } from "./constants";
import { Plus } from "../../../../../components/icons/svg/outline";

/**
 * AddLineItemButton Component
 *
 * Renders a CascadeButton that allows users to add new line items of various types.
 * Contains the logic for creating default line items with appropriate defaults
 * for quantity, unit, and other fields based on the item type.
 *
 * @param {Function} onAddItem - Callback function that receives the new line item object
 */
const AddLineItemButton = ({ onAddItem, disabled }) => {
  /**
   * Handler to create and add new line items
   *
   * Creates a new line item with appropriate defaults based on type:
   * - Labor/Equipment/Services: quantity=1, unit="Hours"
   * - Materials: quantity=0, unit=""
   * - Concrete: quantity=0, unit="Yards"
   *
   * @param {string} type - The type of line item to create
   */
  const buttonType = disabled ? "warning" : "primary";
  const handleAddItemType = (type) => {
    // Set default quantity based on type
    const defaultQuantity = [
      "labor",
      "equipment",
      "subcontractors",
      "dumping",
      "custom_labor",
      "custom_material",
      "custom_equipment",
    ].includes(type)
      ? 1
      : 0;

    // Set default unit based on type
    let defaultUnit = "";
    if (
      type === "labor" ||
      type === "equipment" ||
      type === "subcontractors" ||
      type === "dumping"
    ) {
      defaultUnit = "Hours";
    } else if (type === "concrete") {
      defaultUnit = "Yards";
    } else if (type === "material") {
      defaultUnit = "Count";
    }

    const newItem = {
      id: Date.now(),
      type,
      option_id: null,
      option_name: "",
      quantity: defaultQuantity,
      unit: defaultUnit,
      rate: 0,
    };

    // Add rt_hours for labor items
    if (type === "labor" || type === "custom_labor") {
      newItem.rt_hours = 8;
      newItem.ot_hours = 0;
    }

    onAddItem(newItem);
  };

  return (
    <CascadeButton
      text="Add"
      buttons={{
        Labor: {
          onClick: () => handleAddItemType("labor"),
          color: LINE_ITEM_TYPES.labor.bgColor,
          icon: <Plus className="w-4 h-4" />,
        },
        Material: {
          onClick: () => handleAddItemType("material"),
          color: LINE_ITEM_TYPES.material.bgColor,
          icon: <Plus className="w-4 h-4" />,
        },
        Equipment: {
          onClick: () => handleAddItemType("equipment"),
          color: LINE_ITEM_TYPES.equipment.bgColor,
          icon: <Plus className="w-4 h-4" />,
        },
        Concrete: {
          onClick: () => handleAddItemType("concrete"),
          color: LINE_ITEM_TYPES.concrete.bgColor,
          icon: <Plus className="w-4 h-4" />,
        },
        Subcontractors: {
          onClick: () => handleAddItemType("subcontractors"),
          color: LINE_ITEM_TYPES.subcontractors.bgColor,
          icon: <Plus className="w-4 h-4" />,
        },
        Dumping: {
          onClick: () => handleAddItemType("dumping"),
          color: LINE_ITEM_TYPES.dumping.bgColor,
          icon: <Plus className="w-4 h-4" />,
        },
        "Custom Labor": {
          onClick: () => handleAddItemType("custom_labor"),
          color: LINE_ITEM_TYPES.custom_labor.bgColor,
          icon: <Plus className="w-4 h-4" />,
        },
        "Custom Material": {
          onClick: () => handleAddItemType("custom_material"),
          color: LINE_ITEM_TYPES.custom_material.bgColor,
          icon: <Plus className="w-4 h-4" />,
        },
        "Custom Equipment": {
          onClick: () => handleAddItemType("custom_equipment"),
          color: LINE_ITEM_TYPES.custom_equipment.bgColor,
          icon: <Plus className="w-4 h-4" />,
        },
      }}
      direction="down"
      align="right"
      variant={buttonType}
    />
  );
};

export default AddLineItemButton;
